// Cliente fetch das functions do painel professor.

export interface UserSession {
  id: string;
  email: string;
  name: string;
}
export interface ActiveContext {
  kind: "platform" | "company";
  companyId?: string;
  companyName?: string;
  role: string;
}
export interface UserProfile {
  kind: "platform" | "company";
  role: string;
  companies: Array<{ id: string; name: string }>;
}
export interface MeResponse {
  user: UserSession;
  activeContext: ActiveContext | null;
  contexts: ActiveContext[];
  profiles: UserProfile[];
}

export interface TeacherHome {
  kpis: {
    banksAtivos: number;
    banksArquivados: number;
    turmas: number;
    provasFuturas: number;
    provasLive: number;
    provasTotal: number;
  };
  upcoming: Array<{
    id: string;
    title: string;
    startsAt: string;
    endsAt: string | null;
    status: string;
  }>;
  live: Array<{
    id: string;
    title: string;
    startsAt: string;
    endsAt: string;
    status: string;
  }>;
}

export interface BankRow {
  id: string;
  name: string;
  description: string | null;
  isPublicToCompany: boolean;
  tags: string[];
  version: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  questionCount: number;
  /** Tipos de questão presentes — ["mc"], ["essay"] ou ambos. Vazio se banco vazio. */
  questionKinds?: Array<"mc" | "essay">;
  questionKindCounts?: { mc: number; essay: number };
}

export interface ClassRow {
  id: string;
  name: string;
  description: string | null;
  companyId: string | null;
  tags: string[];
  bankIds: string[];
  memberCount: number;
  bankCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ClassDetail extends ClassRow {
  members: Array<{ id: string; email: string; name: string; apelido: string | null }>;
  banks: Array<{ id: string; name: string }>;
}

export interface DisciplinaRow {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  startsAt: string | null;
  endsAt: string | null;
  gradingEnabled: boolean;
  status: "draft" | "active" | "closed";
  offeringCount: number;
}

export interface DisciplinaDetail extends Omit<DisciplinaRow, "offeringCount"> {
  createdAt: string;
  updatedAt: string;
  offerings: Array<{
    id: string;
    turmaId: string;
    turmaName: string;
    turmaCode: string | null;
    createdAt: string;
  }>;
  students: Array<{
    userId: string;
    email: string;
    name: string;
    turmaId: string;
    turmaName: string;
  }>;
  assessments: Array<{
    id: string;
    offeringId: string | null;
    turmaName: string | null;
    kind: string;
    title: string;
    startsAt: string | null;
    endsAt: string | null;
    releasedAt: string | null;
    status: string;
    createdBy: string | null;
  }>;
}

export interface ExamRow {
  id: string;
  offeringId: string | null;
  title: string;
  startsAt: string | null;
  endsAt: string | null;
  releasedAt: string | null;
  status: string;
  createdAt: string;
}

export interface ExamConfigFull {
  examMode?: "live" | "open_window";
  studentDurationMin?: number;
  allowPause?: boolean;
  subject?: string | null;
  instructions?: string | null;
  hallLeadMin?: number;
  passingGrade?: number;
  showQuestionCountToStudent?: boolean;
  showWeightsToStudent?: boolean;
  autoReleaseResults?: boolean;
  resultReleaseMode?: "after_end" | "fixed";
  resultReleaseOffsetMin?: number;
  resultReleaseAt?: string | null;
  resultDisclosureLevel?: "full" | "score";
  showDisclosureToStudent?: boolean;
  bankNameSnap?: string;
  bankCountSnap?: number;
  offeringNameSnap?: string;
  ownerNameSnap?: string;
}

export interface ExamDetail extends ExamRow {
  description: string | null;
  durationMinutes: number | null;
  weights: number[] | null;
  maxScore: string;
  hasSnapshot?: boolean;
  config: ExamConfigFull;
  offering: { id: string; turmaName: string; disciplinaName: string } | null;
  evaluationItem: {
    id: string;
    disciplinaId: string;
    title: string;
    weight: number;
    kind: string;
  } | null;
  snapshot: {
    bankId: string | null;
    bankName: string | null;
    bankCount: number | null;
    anuladasIdx: number[];
    hasQuestoes: boolean;
  };
  locks: {
    editLockAt: string | null;
    isLocked: boolean;
    lockedFields: readonly string[];
    effectiveReleaseAt: string | null;
  };
  auditLog: Array<{
    id: string;
    action: string;
    details: Record<string, unknown>;
    actorUserId: string | null;
    createdAt: string;
  }>;
}

export interface ExamMonitor {
  exam: {
    id: string;
    title: string;
    startsAt: string | null;
    endsAt: string | null;
    status: string;
  };
  students: Array<{
    userId: string;
    email: string;
    name: string;
    apelido: string | null;
    startedAt: string | null;
    submittedAt: string | null;
    lastSeenAt: string | null;
    online: boolean;
    autoFinalized: boolean;
    answeredCount: number;
    score: string | null;
  }>;
  summary: {
    totalMembros: number;
    conectaram: number;
    onlineAgora: number;
    finalizaram: number;
  };
}

export interface ExamAnswers {
  exam: {
    id: string;
    title: string;
    startsAt: string | null;
    endsAt: string | null;
    status: string;
    releasedAt: string | null;
  };
  questoes: any[];
  anuladasIdx: number[];
  weights: number[] | null;
  perQuestion: Array<{
    idx: number;
    qid: string;
    enunciado: string;
    alternativas: string[];
    correctIdx: number;
    isAnulada: boolean;
    peso: number | null;
    totalRespostas: number;
    acertos: number;
    pctAcerto: number | null;
    distribuicao: Record<
      string,
      {
        count: number;
        respondentes: Array<{ userId: string; name: string; tempoMs: number | null }>;
      }
    >;
  }>;
  students: Array<{
    userId: string;
    email: string;
    name: string;
    startedAt: string;
    submittedAt: string | null;
    answeredCount: number;
    acertos: number;
    scoreOf10: number;
    status: string;
  }>;
}

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {})
    },
    ...init
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {}
  if (!res.ok) {
    const err = body as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(
      res.status,
      err?.error?.code ?? "http_error",
      err?.error?.message ?? `HTTP ${res.status}`
    );
  }
  return body as T;
}

export const api = {
  me: () => call<MeResponse>("/api/auth/me"),
  logout: () => call<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  switchContext: (ctx: { kind: "platform" | "company"; role: string; companyId?: string }) =>
    call<{ ok: true }>("/api/auth/switch-context", {
      method: "POST",
      body: JSON.stringify(ctx)
    }),
  // Teacher
  home: () => call<TeacherHome>("/api/teacher/home"),
  listBanks: () => call<{ banks: BankRow[] }>("/api/teacher/banks"),
  getBank: (id: string) =>
    call<{ bank: BankRow & { payload: any } }>(
      `/api/teacher/banks?id=${encodeURIComponent(id)}`
    ),
  createBank: (data: {
    name: string;
    description?: string | null;
    data: unknown;
    tags?: string[];
    isPublicToCompany?: boolean;
  }) =>
    call<{ bank: { id: string; name: string; version: number }; count: number }>(
      "/api/teacher/banks",
      { method: "POST", body: JSON.stringify(data) }
    ),
  patchBank: (id: string, updates: any) =>
    call<{ ok: true }>("/api/teacher/banks", {
      method: "PATCH",
      body: JSON.stringify({ id, ...updates })
    }),
  deleteBank: (id: string) =>
    call<{ deleted: string }>(`/api/teacher/banks?id=${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  // Teacher bank grants (acesso direto user↔banco, escopo do prof)
  bankGrantsMatrix: () =>
    call<{
      banks: { id: string; name: string; archived: boolean }[];
      students: {
        id: string;
        email: string;
        name: string;
        apelido: string | null;
      }[];
      grants: { bankId: string; userId: string; canTakeExam: boolean }[];
    }>("/api/teacher/bank-grants"),
  bankGrantsByBank: (bankId: string) =>
    call<{
      bank: { id: string; name: string };
      entries: {
        userId: string;
        email: string;
        name: string;
        apelido: string | null;
        canTakeExam: boolean;
        grantedAt: string;
      }[];
    }>(`/api/teacher/bank-grants?bankId=${encodeURIComponent(bankId)}`),
  putBankGrant: (body: {
    bankId: string;
    userId: string;
    canTakeExam?: boolean;
  }) =>
    call<{
      bankId: string;
      userId: string;
      canTakeExam: boolean;
      grantedAt: string;
    }>("/api/teacher/bank-grants", {
      method: "PUT",
      body: JSON.stringify(body)
    }),
  deleteBankGrant: (bankId: string, userId: string) =>
    call<{ ok: true }>(
      `/api/teacher/bank-grants?bankId=${encodeURIComponent(
        bankId
      )}&userId=${encodeURIComponent(userId)}`,
      { method: "DELETE" }
    ),
  listClasses: () => call<{ classes: ClassRow[] }>("/api/teacher/classes"),
  getClass: (id: string) =>
    call<{ class: ClassDetail }>(`/api/teacher/classes?id=${encodeURIComponent(id)}`),
  createClass: (data: any) =>
    call<{ class: ClassDetail }>("/api/teacher/classes", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  patchClass: (id: string, data: any) =>
    call<{ class: ClassDetail }>(`/api/teacher/classes?id=${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(data)
    }),
  deleteClass: (id: string) =>
    call<{ deleted: string }>(`/api/teacher/classes?id=${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  syncClassGrants: (id: string) =>
    call<{ ok: true; members: number; banks: number; granted: number; skipped: number }>(
      `/api/teacher/classes?id=${encodeURIComponent(id)}&action=sync-grants`,
      { method: "POST" }
    ),
  listStudents: (classId?: string) =>
    call<{ students: any[]; total: number; classes: Array<{ id: string; name: string }> }>(
      `/api/teacher/students${classId ? `?classId=${encodeURIComponent(classId)}` : ""}`
    ),
  listDisciplinas: () =>
    call<{ disciplinas: DisciplinaRow[] }>("/api/teacher/disciplinas"),
  getDisciplina: (id: string) =>
    call<{ disciplina: DisciplinaDetail }>(
      `/api/teacher/disciplinas?id=${encodeURIComponent(id)}`
    ),
  listExams: () => call<{ exams: ExamRow[] }>("/api/teacher/exams"),
  getExam: (id: string) =>
    call<{ exam: ExamDetail }>(`/api/teacher/exams?id=${encodeURIComponent(id)}`),
  createExam: (data: any) =>
    call<{ exam: ExamDetail }>("/api/teacher/exams", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  patchExam: (id: string, data: any) =>
    call<{ exam: ExamDetail }>(`/api/teacher/exams?id=${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(data)
    }),
  deleteExam: (id: string) =>
    call<{ deleted: string }>(`/api/teacher/exams?id=${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  // ===== Avaliação composta da disciplina =====
  getDisciplinaEvaluation: (disciplinaId: string) =>
    call<DisciplinaEvaluationSnapshot>(
      `/api/teacher/disciplina-evaluation?disciplinaId=${encodeURIComponent(disciplinaId)}`
    ),
  patchDisciplinaEvaluation: (
    disciplinaId: string,
    data: {
      passingGrade?: number;
      recoveryGrade?: number;
      recoveryEnabled?: boolean;
    }
  ) =>
    call<DisciplinaEvaluationSnapshot>(
      `/api/teacher/disciplina-evaluation?disciplinaId=${encodeURIComponent(disciplinaId)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  createEvaluationItem: (
    disciplinaId: string,
    data: {
      kind: "prova" | "atividade";
      title: string;
      description?: string;
      weight: number;
      assessmentId?: string | null;
    }
  ) =>
    call<DisciplinaEvaluationSnapshot & { createdItemId: string }>(
      `/api/teacher/disciplina-evaluation?disciplinaId=${encodeURIComponent(disciplinaId)}`,
      { method: "POST", body: JSON.stringify(data) }
    ),
  patchEvaluationItem: (
    itemId: string,
    data: {
      title?: string;
      description?: string | null;
      weight?: number;
      position?: number;
      kind?: "prova" | "atividade";
      assessmentId?: string | null;
    }
  ) =>
    call<DisciplinaEvaluationSnapshot>(
      `/api/teacher/disciplina-evaluation?itemId=${encodeURIComponent(itemId)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  deleteEvaluationItem: (itemId: string) =>
    call<DisciplinaEvaluationSnapshot>(
      `/api/teacher/disciplina-evaluation?itemId=${encodeURIComponent(itemId)}`,
      { method: "DELETE" }
    ),
  setEvaluationGrade: (
    itemId: string,
    userId: string,
    data: { score: number; comment?: string | null }
  ) =>
    call<DisciplinaEvaluationSnapshot>(
      `/api/teacher/disciplina-evaluation?itemId=${encodeURIComponent(
        itemId
      )}&userId=${encodeURIComponent(userId)}`,
      { method: "PUT", body: JSON.stringify(data) }
    ),
  deleteEvaluationGrade: (itemId: string, userId: string) =>
    call<DisciplinaEvaluationSnapshot>(
      `/api/teacher/disciplina-evaluation?itemId=${encodeURIComponent(
        itemId
      )}&userId=${encodeURIComponent(userId)}`,
      { method: "DELETE" }
    ),
  anularQuestao: (examId: string, questaoIdx: number, unanular = false) =>
    call<{ ok: true; anuladasIdx: number[]; attemptsUpdated: number; attemptsTotal: number }>(
      `/api/teacher/exams?id=${encodeURIComponent(examId)}&action=anular`,
      { method: "POST", body: JSON.stringify({ questaoIdx, unanular }) }
    ),
  releaseExam: (examId: string) =>
    call<{ exam: ExamDetail }>(
      `/api/teacher/exams?id=${encodeURIComponent(examId)}&action=release`,
      { method: "POST", body: "{}" }
    ),
  extendExam: (examId: string, opts: { newEndsAt?: string; addMinutes?: number }) =>
    call<{ exam: ExamDetail }>(
      `/api/teacher/exams?id=${encodeURIComponent(examId)}&action=extend`,
      { method: "POST", body: JSON.stringify(opts) }
    ),
  forceEndExam: (examId: string) =>
    call<{ exam: ExamDetail }>(
      `/api/teacher/exams?id=${encodeURIComponent(examId)}&action=force_end`,
      { method: "POST", body: "{}" }
    ),
  announceExam: (examId: string, message: string) =>
    call<{ ok: true }>(
      `/api/teacher/exams?id=${encodeURIComponent(examId)}&action=announce`,
      { method: "POST", body: JSON.stringify({ message }) }
    ),
  resetAttempt: (examId: string, userId: string, reason?: string) =>
    call<{ ok: true }>(
      `/api/teacher/exams?id=${encodeURIComponent(examId)}&action=reset_attempt`,
      { method: "POST", body: JSON.stringify({ userId, reason: reason ?? null }) }
    ),
  examMonitor: (examId: string) =>
    call<ExamMonitor>(`/api/teacher/exam-monitor?examId=${encodeURIComponent(examId)}`),
  examAnswers: (examId: string) =>
    call<ExamAnswers>(`/api/teacher/exam-answers?examId=${encodeURIComponent(examId)}`),
  // ===== Pacotes =====
  listPacotes: (disciplinaId: string) =>
    call<{ pacotes: PacoteRow[] }>(
      `/api/teacher/pacotes?disciplinaId=${encodeURIComponent(disciplinaId)}`
    ),
  createPacote: (data: PacoteCreate) =>
    call<{ pacote: PacoteRow }>("/api/teacher/pacotes", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  patchPacote: (id: string, data: PacotePatch) =>
    call<{ pacote: PacoteRow }>(
      `/api/teacher/pacotes?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  archivePacote: (id: string) =>
    call<{ archived: string }>(
      `/api/teacher/pacotes?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  // ===== Aulas (agenda) =====
  listAulas: (params: { offeringId?: string; from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (params.offeringId) q.set("offeringId", params.offeringId);
    if (params.from) q.set("from", params.from);
    if (params.to) q.set("to", params.to);
    return call<{ aulas: AulaRow[] }>(`/api/teacher/aulas?${q.toString()}`);
  },
  agendarAula: (data: AulaCreate) =>
    call<{ aula: AulaRow }>("/api/teacher/aulas", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  agendarRecorrencia: (data: AulaRecorCreate) =>
    call<{ regra: any; aulas: AulaRow[] }>(
      "/api/teacher/aulas?action=recur",
      { method: "POST", body: JSON.stringify(data) }
    ),
  patchAula: (
    id: string,
    data: { dataHora?: string; duracaoMin?: number; descricao?: string | null; notify?: boolean }
  ) =>
    call<{ aula: AulaRow }>(`/api/teacher/aulas?id=${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(data)
    }),
  realizeAula: (id: string, opts: { realizedAt?: string } = {}) =>
    call<{ aula: AulaRow; saldo: number }>(
      `/api/teacher/aulas?id=${encodeURIComponent(id)}&action=realize`,
      { method: "POST", body: JSON.stringify(opts) }
    ),
  cancelAula: (id: string, reason?: string, notify = true) =>
    call<{ aula: AulaRow }>(
      `/api/teacher/aulas?id=${encodeURIComponent(id)}&action=cancel`,
      { method: "POST", body: JSON.stringify({ reason, notify }) }
    ),
  // ===== Notificações =====
  listNotifications: (opts: { onlyUnread?: boolean; includeHidden?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.onlyUnread) q.set("onlyUnread", "1");
    if (opts.includeHidden) q.set("include", "hidden");
    const qs = q.toString();
    return call<{ notifications: NotificationRow[]; unreadCount: number }>(
      `/api/notifications${qs ? "?" + qs : ""}`
    );
  },
  markNotifRead: (id: string) =>
    call<{ notification: NotificationRow }>(
      `/api/notifications?id=${encodeURIComponent(id)}&action=read`,
      { method: "PATCH" }
    ),
  markNotifUnread: (id: string) =>
    call<{ notification: NotificationRow }>(
      `/api/notifications?id=${encodeURIComponent(id)}&action=unread`,
      { method: "PATCH" }
    ),
  hideNotif: (id: string) =>
    call<{ notification: NotificationRow }>(
      `/api/notifications?id=${encodeURIComponent(id)}&action=hide`,
      { method: "PATCH" }
    ),
  markAllNotifsRead: () =>
    call<{ ok: true }>(`/api/notifications?action=read-all`, {
      method: "PATCH"
    }),
  // ===== Mensagens (broadcast prof→turma) =====
  listSentMessages: (offeringId: string) =>
    call<{ messages: SentMessageRow[] }>(
      `/api/teacher/messages?offeringId=${encodeURIComponent(offeringId)}`
    ),
  sendBroadcast: (data: {
    offeringId: string;
    title: string;
    body: string;
    notify?: boolean;
  }) =>
    call<{ message: SentMessageRow; recipients: number }>(
      "/api/teacher/messages",
      { method: "POST", body: JSON.stringify(data) }
    ),
  // ===== Conteúdos =====
  listContents: (params: { offeringId?: string; disciplinaId?: string }) => {
    const q = new URLSearchParams();
    if (params.offeringId) q.set("offeringId", params.offeringId);
    if (params.disciplinaId) q.set("disciplinaId", params.disciplinaId);
    return call<{ contents: TeacherContentRow[] }>(
      `/api/teacher/contents?${q.toString()}`
    );
  },
  createContent: (data: {
    offeringId: string;
    kind: "file" | "folder";
    name: string;
    url?: string;
    mime?: string;
    sizeBytes?: number;
    notify?: boolean;
  }) =>
    call<{ content: TeacherContentRow }>("/api/teacher/contents", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  patchContent: (id: string, data: { name?: string; url?: string }) =>
    call<{ content: TeacherContentRow }>(
      `/api/teacher/contents?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  deleteContent: (id: string) =>
    call<{ deleted: string }>(
      `/api/teacher/contents?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  // ===== Webhooks (do próprio usuário) =====
  listWebhooks: () =>
    call<{
      scope: "teacher" | "student";
      allowedEvents: string[];
      subscriptions: UserWebhookSubscription[];
      events: UserWebhookEventLog[];
    }>("/api/user/webhooks"),
  createWebhook: (data: { url: string; events?: string[]; description?: string }) =>
    call<{ subscription: UserWebhookSubscription; secret: string }>(
      "/api/user/webhooks",
      { method: "POST", body: JSON.stringify(data) }
    ),
  rotateWebhookSecret: (id: string) =>
    call<{ secret: string }>(
      `/api/user/webhooks?id=${encodeURIComponent(id)}&action=rotate-secret`,
      { method: "POST" }
    ),
  patchWebhook: (
    id: string,
    data: { url?: string; events?: string[]; active?: boolean; description?: string }
  ) =>
    call<{ subscription: UserWebhookSubscription }>(
      `/api/user/webhooks?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  deleteWebhook: (id: string) =>
    call<{ ok: true }>(`/api/user/webhooks?id=${encodeURIComponent(id)}`, {
      method: "DELETE"
    })
};

export interface UserWebhookSubscription {
  id: string;
  url: string;
  secretPreview?: string;
  events: string[];
  active: boolean;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserWebhookEventLog {
  id: string;
  subscriptionId: string;
  kind: string;
  status: "pending" | "delivered" | "failed";
  attempts: number;
  lastStatusCode: number | null;
  lastResponseMs: number | null;
  lastError: string | null;
  createdAt: string;
  deliveredAt: string | null;
  lastAttemptAt: string | null;
}

export interface SentMessageRow {
  id: string;
  offeringId: string;
  fromUserId: string;
  title: string;
  body: string;
  toUserId: string | null;
  createdAt: string;
}

export interface TeacherContentRow {
  id: string;
  offeringId: string;
  parentId: string | null;
  kind: "folder" | "file" | "bank_ref";
  name: string;
  payload: Record<string, unknown>;
  releaseAt: string | null;
  createdAt: string;
}

// ===== Notificações =====
export type NotificationKind =
  | "payment_received"
  | "payment_pending"
  | "lesson_scheduled"
  | "lesson_logged"
  | "lesson_canceled"
  | "lesson_reminder"
  | "credit_low"
  | "credit_zero"
  | "enrolled_in_offering"
  | "assigned_to_disciplina"
  | "content_added"
  | "message_received"
  | "system";

export interface NotificationRow {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string | null;
  payload: Record<string, unknown> | null;
  linkUrl: string | null;
  readAt: string | null;
  hiddenAt: string | null;
  createdAt: string;
}

// ===== Tipos de Pacote =====
export interface PacoteRow {
  id: string;
  disciplinaId: string;
  nome: string;
  descricao: string | null;
  numAulas: number;
  precoCents: number;
  ativo: boolean;
  ordem: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface PacoteCreate {
  disciplinaId: string;
  nome: string;
  descricao?: string | null;
  numAulas: number;
  precoCents: number;
  ativo?: boolean;
  ordem?: number;
}
export interface PacotePatch {
  nome?: string;
  descricao?: string | null;
  numAulas?: number;
  precoCents?: number;
  ativo?: boolean;
  ordem?: number;
}

// ===== Tipos de Aula =====
export interface AulaRow {
  id: string;
  offeringId: string;
  alunoUserId: string;
  professorUserId: string;
  dataHora: string;
  duracaoMin: number;
  status: "scheduled" | "realized" | "canceled" | "no_show";
  creditosConsumidos: number;
  descricao: string | null;
  recorrenciaGrupoId: string | null;
  realizedAt: string | null;
  canceledAt: string | null;
  canceledReason: string | null;
  createdAt: string;
}
export interface AulaCreate {
  offeringId: string;
  alunoUserId: string;
  dataHora: string;
  duracaoMin?: number;
  descricao?: string;
  notify?: boolean;
}
export interface AulaRecorCreate {
  offeringId: string;
  alunoUserId: string;
  freq: "weekly" | "biweekly" | "monthly";
  diasSemana?: number[];
  hora: string;
  duracaoMin?: number;
  inicio: string;
  fim?: string;
  horizonteMeses?: number;
  notify?: boolean;
}

// ===== Avaliação composta da disciplina =====

export interface DisciplinaEvaluationItem {
  id: string;
  kind: "prova" | "atividade";
  title: string;
  description: string | null;
  weight: number;
  position: number;
  assessmentId: string | null;
  assessmentTitle: string | null;
  assessmentReleasedAt: string | null;
  createdAt: string;
}

export interface DisciplinaEvaluationStudent {
  userId: string;
  name: string;
  email: string;
  grades: Record<string, number | null>;
  finalScore: number;
  status: "aprovado" | "recuperacao" | "reprovado" | "incompleto";
}

export interface DisciplinaEvaluationSnapshot {
  disciplina: {
    id: string;
    name: string;
    passingGrade: number;
    recoveryGrade: number;
    recoveryEnabled: boolean;
  };
  items: DisciplinaEvaluationItem[];
  students: DisciplinaEvaluationStudent[];
  weightSum: number;
}
